import { DeleteOutlined } from "@ant-design/icons";
import { Button, Form, message, Modal, Space, Table, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useMemo, useState } from "react";
import { TransferListingItem } from "../Create";
import SelectEmployee from "../../../components/form/SelectEmployee";
import SelectOffice from "../../../components/form/SelectOffice";
import { generateFormRules } from "../../../helpers/formRules";
import { EEmployeeRole } from "../../../types/employee.type";
import { OfficeProperties } from "../../../types/office.type";
import { UserProperties } from "../../../types/user.type";
import EmployeeInformation from "./EmployeeInformation";
import NotSet from "../../../components/NotSet";
import { EEmployeeRoleStatus } from "../../../types/employeeRole.type";

export interface AssignAgentModalCallback {
	selectedListing: TransferListingItem[];
	newOwner: {
		name: string;
		profilePic?: string;
		officeName?: string;
		officeId: string;
		userId: string;
		employeeId: string;
	};
}
interface AssignAgentModalProps {
	isSingle?: boolean;
	open?: boolean;
	onClose?: () => void;
	selectedListing?: TransferListingItem[];
	onUnselect?: (listingId: string) => void;
	onSubmit?: (value: AssignAgentModalCallback) => void;
	isTransferListing?: boolean;
	officeIdProps?: string;
}
interface Values {
	officeId: string;
	userId: string;
}
const { Text } = Typography;
const AssignAgentModal: React.FC<AssignAgentModalProps> = ({
	isSingle,
	open,
	onClose,
	selectedListing,
	onUnselect,
	onSubmit,
	isTransferListing,
	officeIdProps,
}) => {
	const [form] = Form.useForm<Values>();
	const [officeList, setOfficeList] = useState<OfficeProperties[]>([]);
	const [userList, setUserList] = useState<UserProperties[]>([]);

	const totalListing = useMemo(() => (selectedListing ? selectedListing.length : 0), [selectedListing]);

	const COLUMNS: ColumnsType<TransferListingItem> = [
		{
			title: "Listing ID",
			dataIndex: "listingCode",
			key: "listingCode",
			width: 100,
			render: (v: string) => v?.toUpperCase() ?? <NotSet />,
		},
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
		},
		{
			title: "City",
			dataIndex: "city",
			key: "city",
			// ellipsis: true,
		},
		!isSingle
			? {
					title: "",
					dataIndex: "listingId",
					key: "listingId",
					width: 50,
					render: (listingId: string) => {
						if ((selectedListing ?? []).length > 1) {
							return (
								<Button
									type="text"
									danger={true}
									icon={<DeleteOutlined />}
									size="small"
									onClick={() => onUnselect?.(listingId)}
								/>
							);
						}
					},
			  }
			: null,
	].filter(Boolean) as ColumnsType<TransferListingItem>;

	const officeIdWatch = Form.useWatch("officeId", form);
	const userId = Form.useWatch("userId", form);

	const officeId = (isTransferListing ? officeIdProps : officeIdWatch) ?? "";

	useEffect(() => {
		if (officeIdProps) {
			form.setFieldValue("officeId", officeIdProps);
		}
	}, [officeIdProps]);

	const selectedUser = useMemo(() => {
		if (userId) {
			return userList.find((item) => item.userId === userId);
		}
		return undefined;
	}, [userId, userList]);

	const _handleSubmit = (values: Values) => {
		if (!selectedUser) return message.error("User not selected!");

		let employeeId = selectedUser?.employee?.[0].employeeId;
		if (!employeeId) return message.error("Something went wrong, employeeid cannot defined!");
		if (!selectedListing) return message.error("No listing selected!");

		let selectedOffice = officeList.find((item) => item.officeId === officeId);

		let callback: AssignAgentModalCallback = {
			selectedListing,
			newOwner: {
				name: selectedUser?.nickName!,
				profilePic: selectedUser?.profilePic,
				officeName: selectedOffice?.officeName,
				userId: values.userId,
				officeId,
				employeeId: employeeId,
			},
		};

		onSubmit?.(callback);
		onClose?.();
	};

	return (
		<Modal
			width={535}
			open={open}
			onCancel={onClose}
			title="Select Agent"
			destroyOnClose={true}
			okText={"Transfer " + totalListing + " Listing"}
			onOk={form.submit}
		>
			<Space
				direction="vertical"
				size="large"
			>
				<Text type="secondary">Select agent that will be owner of this listing</Text>

				<Table
					dataSource={selectedListing}
					columns={COLUMNS}
					scroll={{
						y: 200,
					}}
					footer={() =>
						isSingle ? undefined : (
							<>
								<Text
									type="secondary"
									style={{ marginRight: 10 }}
								>
									Total Listing
								</Text>{" "}
								{totalListing}
							</>
						)
					}
					size="small"
					pagination={false}
					rowKey="listingId"
				/>
				<Form
					form={form}
					preserve={false}
					layout="vertical"
					onFinish={_handleSubmit}
				>
					{isTransferListing ? (
						<Form.Item
							name="officeId"
							noStyle={true}
						></Form.Item>
					) : (
						<Form.Item
							name="officeId"
							label="Select Office"
							rules={generateFormRules("Office", ["required"])}
						>
							<SelectOffice
								officeList={officeList}
								setOfficeList={setOfficeList}
							/>
						</Form.Item>
					)}

					<Form.Item
						name="userId"
						label="Select Agent"
						rules={generateFormRules("Agent", ["required"])}
					>
						<SelectEmployee
							officeId={officeId}
							setUserList={setUserList}
							userList={userList}
							customQuery={{
								filterRole: EEmployeeRole.agent,
								filterRoleStatus: EEmployeeRoleStatus.PUBLISH,
							}}
							excludeUserId={selectedListing?.[0]?.agent?.userId ?? undefined}
						/>
					</Form.Item>

					<EmployeeInformation
						user={selectedUser}
						isSmall={true}
						isTransferListing={isTransferListing}
					/>
				</Form>
			</Space>
		</Modal>
	);
};
export default AssignAgentModal;
